import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { authenticateTokiUser, getProfilePictureUrl, PAYMENT_STATUS } from '../utils/tokiApi';
import { isTokiWebView, setColor, getPlatform } from '../utils/tokiBridge';
import { getStoredUser, isAuthenticated as checkAuth } from '../api/services/auth';
import { getToken, setToken, setRefreshToken, removeToken, removeRefreshToken, clearAllAuth, attemptTokenRefresh } from '../api/client';
import { getMyProfile } from '../api/services/candidate';
import { API_BASE_URL } from '../api/config';
import { isHambiDomain } from '../config/region';
import { isJwtExpired } from '../utils/jwtUtils';
import { createSessionManager } from '../utils/sessionManager';
export const TokiContext = createContext();

/**
 * URL-аас token авах (token эсвэл tokenid параметр)
 * locale байвал Hambi гэж үзээд skip хийнэ
 */
const getTokenFromParams = (params) => {
    return (
        params.get('token') ||
        params.get('tokenid') ||
        params.get('x-user-token') ||
        params.get('x_user_token') ||
        params.get('xUserToken')
    );
};

const getTokiTokenFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);

    // locale параметр байвал Hambi-аас ирсэн, Toki биш
    if (urlParams.has('locale')) {
        return null;
    }

    // Hambi домэйн (ajob.uz, ajob.kz) дээрх token-ууд Hambi-д хамаарна
    if (isHambiDomain()) {
        return null;
    }

    const tokenFromSearch = getTokenFromParams(urlParams);
    if (tokenFromSearch) return tokenFromSearch;

    // Some Toki webviews put params in hash (/#/?token=...)
    const hash = window.location.hash || '';
    const hashQueryIndex = hash.indexOf('?');
    if (hashQueryIndex !== -1) {
        const hashParams = new URLSearchParams(hash.slice(hashQueryIndex + 1));
        const tokenFromHash = getTokenFromParams(hashParams);
        if (tokenFromHash) return tokenFromHash;
    }

    return null;
};

// Session manager for Toki (shared utility)
const tokiSession = createSessionManager('toki_session');

/**
 * Профайлын кэш (утасны дугаараар нэвтэрсэн хэрэглэгчид)
 *
 * Ачаалахад толгой хэсгийн зураг API-гаас ирэхийг хүлээгээд хоосон байсан —
 * хариу удаан ирвэл (эсвэл алдаа гарвэл) хэрэглэгч refresh хийж байж зургаа
 * хардаг байлаа. localStorage-д хадгалснаар эхний render дээрээ шууд харагдана,
 * шинэ өгөгдөл ирэхэд нь чимээгүйхэн шинэчилнэ.
 */
const PROFILE_CACHE_KEY = 'diil_profile_cache';

const readProfileCache = () => {
    try {
        const raw = localStorage.getItem(PROFILE_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const writeProfileCache = (data) => {
    try {
        if (data) {
            localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(data));
        } else {
            localStorage.removeItem(PROFILE_CACHE_KEY);
        }
    } catch (e) {
        console.warn('Failed to cache profile:', e);
    }
};

// Helper to convert relative profile URL to full URL
const getFullProfileUrl = (profileUrl) => {
    if (!profileUrl) return null;
    if (profileUrl.startsWith('http')) return profileUrl;
    return `${API_BASE_URL}${profileUrl.startsWith('/') ? '' : '/'}${profileUrl}`;
};

/**
 * TokiProvider Component
 */
export const TokiProvider = ({ children }) => {
    // Toki user state (from Toki app)
    const [user, setUser] = useState(null);
    const [userToken, setUserToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Phone auth user state (from phone OTP login)
    const [phoneUser, setPhoneUser] = useState(null);

    // Profile data from API (for phone users)
    const [profileData, setProfileData] = useState(() => readProfileCache());

    // Platform info - initialize synchronously so it's correct from first render
    const [platform, setPlatform] = useState(() => getPlatform());
    const [isInTokiApp, setIsInTokiApp] = useState(() => isTokiWebView());

    // Whether user came from Toki (URL token or stored session) — used for auth decisions
    // Separate from isInTokiApp (WebView bridge detection for UI layout)
    const [isTokiUser, setIsTokiUser] = useState(() => !!tokiSession.get());

    // Store URL token for retry attempts
    const urlTokenRef = useRef(null);

    /**
     * Хэрэглэгчийн мэдээлэл авах
     */
    const fetchUserInfo = useCallback(async (token) => {
        if (!token) {
            setIsLoading(false);
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Same clean pattern as Hambi's authenticateHambiUser
            const result = await authenticateTokiUser(token);

            if (result.success) {
                // Extract user data (may be in result.data or at top level)
                const rawUserData = result.data;
                const userData = {
                    ...rawUserData,
                    profilePictureFullUrl: getProfilePictureUrl(rawUserData?.profilePicURL)
                };

                // Store JWT directly from response (same as Hambi)
                if (result.token) {
                    setToken(result.token);
                }
                if (result.refreshToken) {
                    setRefreshToken(result.refreshToken);
                }

                setUser(userData);
                setUserToken(token);
                tokiSession.save(userData, token);

                setIsLoading(false);
                return userData;
            } else {
                console.error('Toki API error:', result.error);
                setError(result.error);
                setIsLoading(false);
                return null;
            }
        } catch (err) {
            console.error('Toki fetch error:', err);
            setError(err.message || 'Холболтын алдаа');
            setIsLoading(false);
            return null;
        }
    }, []);

    /**
     * Header өнгө тохируулах
     */
    const setHeaderColor = useCallback((color, isStatusDark = true) => {
        return setColor(color, isStatusDark);
    }, []);

    /**
     * Logout функц
     */
    const logout = useCallback(() => {
        setUser(null);
        setUserToken(null);
        setPhoneUser(null);
        setProfileData(null);
        writeProfileCache(null);
        setIsTokiUser(false);
        clearAllAuth(); // Clears all tokens, sessions, and user data
    }, []);

    /**
     * Toki re-authentication
     * JWT дууссан эсвэл алдаа гарсан үед Toki token-оор дахин нэвтрэх
     * Returns true if re-auth succeeded, false otherwise
     */
    const retryAuth = useCallback(async () => {
        // Try URL token first (freshest), then stored session, then in-memory token
        // In-memory fallback is needed because clearAllAuth() may have cleared localStorage
        const storedSession = tokiSession.get();
        const retryToken = urlTokenRef.current || storedSession?.token || userToken;

        if (!retryToken) {
            return false;
        }

        const result = await fetchUserInfo(retryToken);
        return !!result;
    }, [fetchUserInfo, userToken]);

    /**
     * Initialize - App эхлэхэд ажиллана
     */
    useEffect(() => {
        const init = async () => {
            // Platform тодорхойлох
            const currentPlatform = getPlatform();
            setPlatform(currentPlatform);
            setIsInTokiApp(isTokiWebView());

            // URL-аас token авах
            const urlToken = getTokiTokenFromUrl();

            if (urlToken) {
                // Store URL token for retry attempts
                urlTokenRef.current = urlToken;
                setIsTokiUser(true);
                // URL-д token байвал шинээр fetch хийнэ
                let result = await fetchUserInfo(urlToken);

                // Retry once after delay if first attempt failed
                // (handles Cloud Run cold start / Toki token propagation delay)
                if (!result) {
                    await new Promise(resolve => setTimeout(resolve, 1500));
                    result = await fetchUserInfo(urlToken);
                }
            } else {
                // URL-д token байхгүй бол localStorage-аас сэргээнэ
                const storedSession = tokiSession.get();
                if (storedSession) {
                    setIsTokiUser(true);

                    // Restore user from stored session immediately (shows name/avatar while JWT refreshes)
                    setUser(storedSession.user);
                    setUserToken(storedSession.token);

                    const existingJwt = getToken();
                    const jwtValid = existingJwt && !isJwtExpired(existingJwt);

                    const refreshJwt = async () => {
                        // Try Toki API first, fall back to standard /auth/refresh
                        try {
                            const result = await authenticateTokiUser(storedSession.token);
                            if (result.success) {
                                if (result.token) setToken(result.token);
                                if (result.refreshToken) setRefreshToken(result.refreshToken);
                                return;
                            }
                        } catch (err) {
                            console.warn('Toki API refresh failed, trying standard refresh...');
                        }
                        // Fallback: use /auth/refresh with the long-lived refresh token (365 days)
                        await attemptTokenRefresh();
                    };

                    if (jwtValid) {
                        // JWT still valid — ready immediately, refresh in background
                        setIsLoading(false);
                        refreshJwt();
                    } else {
                        // JWT expired — must wait for refresh before components can fetch
                        refreshJwt().finally(() => setIsLoading(false));
                    }
                } else {
                    // Check for stored phone user (from phone OTP login)
                    const storedUser = getStoredUser();
                    if (storedUser && checkAuth()) {
                        setPhoneUser(storedUser);

                        // Proactively refresh JWT if expired (same pattern as Toki/Hambi)
                        const existingJwt = getToken();
                        const jwtValid = existingJwt && !isJwtExpired(existingJwt);

                        if (!jwtValid) {
                            // JWT expired — refresh before components make API calls
                            attemptTokenRefresh()
                                .then(newToken => {
                                    if (!newToken) setPhoneUser(null);
                                })
                                .finally(() => setIsLoading(false));
                            return; // Don't setIsLoading(false) below
                        }
                    }
                    setIsLoading(false);
                }
            }
        };

        init();
    }, [fetchUserInfo]);

    // Clear React state when session is fully expired (tokens cleared by client.js)
    // Without this, isAuthenticated stays true after clearAllAuth() clears localStorage
    useEffect(() => {
        const handleSessionExpired = () => {
            setUser(null);
            setUserToken(null);
            setPhoneUser(null);
            setProfileData(null);
            writeProfileCache(null);
            setIsTokiUser(false);
        };
        window.addEventListener('auth:sessionExpired', handleSessionExpired);
        return () => window.removeEventListener('auth:sessionExpired', handleSessionExpired);
    }, []);

    // Fetch profile data when phone user is authenticated.
    // Кэшлэгдсэн профайл байвал зураг аль хэдийн харагдаж байгаа тул энэ нь
    // зөвхөн шинэчлэлт. Эхний оролдлого амжилтгүй болбол (JWT дөнгөж сэргээгдэж
    // байх зэрэг) нэг удаа дахин оролдоно — эс бөгөөс хэрэглэгч refresh хийх
    // хүртэл дахин татахгүй байсан.
    useEffect(() => {
        if (!phoneUser || user) return;

        let cancelled = false;

        const loadProfile = async () => {
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    // Use JWT-based getMyProfile from hrSystemServer
                    const result = await getMyProfile();
                    if (cancelled) return;

                    // hrSystemServer returns: { found: true, candidate: { photoUrl, ... } }
                    if (result.success && result.data) {
                        // Extract candidate object from response
                        const candidateData = result.data.candidate || result.data;

                        setProfileData(candidateData);
                        writeProfileCache(candidateData);
                        return;
                    }
                    console.warn('📸 Profile fetch failed:', result.error);
                } catch (error) {
                    if (cancelled) return;
                    console.error('📸 Failed to fetch profile data:', error);
                }

                if (attempt === 0) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    if (cancelled) return;
                }
            }
        };

        loadProfile();
        return () => { cancelled = true; };
    }, [phoneUser, user]);

    // Get profile photo URL from hrSystemServer profile data
    /**
     * Профайлын зураг шинэчлэгдэхэд (upload) кэш болон толгой хэсгийг шууд
     * шинэчилнэ — эс бөгөөс дахин ачаалах хүртэл хуучин зураг харагдана.
     */
    const setProfilePhoto = useCallback((photoUrl) => {
        setProfileData((prev) => {
            const next = { ...(prev || {}), photoUrl };
            writeProfileCache(next);
            return next;
        });
    }, []);

    const getPhoneUserPhotoUrl = useCallback(() => {
        // hrSystemServer returns photoUrl (camelCase)
        // hrSystemServer already returns full GCS URL, no need to prepend base URL
        // Нэвтрэхэд хадгалагдсан хэрэглэгч дээр зураг байвал профайл татагдах
        // хүртэл түүнийг харуулна.
        return profileData?.photoUrl || phoneUser?.photoUrl || null;
    }, [profileData, phoneUser]);

    // Combined user (Toki user or phone user)
    const currentUser = user || phoneUser;

    // Context value
    const value = {
        // User info (Toki)
        user,
        userToken,
        isLoading,
        error,

        // Phone auth user
        phoneUser,
        setPhoneUser,

        // Profile data from API (for phone users)
        profileData,
        getPhoneUserPhotoUrl,
        setProfilePhoto,

        // Combined auth state
        currentUser,
        isAuthenticated: !!(user || phoneUser),

        // Platform info
        platform,
        isInTokiApp,
        isTokiUser,

        // Functions
        fetchUserInfo,
        retryAuth,
        setHeaderColor,
        logout,

        // Constants
        PAYMENT_STATUS
    };

    return (
        <TokiContext.Provider value={value}>
            {children}
        </TokiContext.Provider>
    );
};

/**
 * useToki Hook
 * Toki context-г хялбар ашиглах
 */
export const useToki = () => {
    const context = useContext(TokiContext);

    if (!context) {
        throw new Error('useToki must be used within a TokiProvider');
    }

    return context;
};

export default TokiContext;
